import {Transaction} from "./Transaction";
import { TransactionType, Cols, ICompValue, IValue, IHolding } from "../shared/Consts";
import { Price } from "./Price";
import Portfolio from "./Portfolio";
import IDividend from "./IDividend";
import createTrans from "./TransFactory";
import createHolding from "./HoldingFactory";
import DividendHistory from "../portlibs/DividendHistory";
import Prices from "./Prices";
import { ITransRow } from "../shared/ITransaction";
import PortUtils from "../portlibs/PortUtils";
import PortAccount from "./PortAccount";

export default class Holding implements ICompValue, IHolding, IValue {
    portfolio:Portfolio;
    id:string;
    name:string;
    quantity: number;
    averagePrice: number;
    dividend: number;
    /**
     * monetary cost
     */
    originalCost: number;
    adjustments: number;
    transactions:Transaction[];

    // calculated after price

    /**
     * average price * holding qunaity
     */
    holdingCost: number;
    /**
     * sum of all transactions including dispositions
     */
    sumTransaction: number;
    /**
     * sumTransaction + current holding value
     */
    gain: number;

    /**
     * holding value - holding cost
     */
    ugain: number;

    /**
     * totalDividend - reinvest
     */
    excessDiv: number;

    profit: number;

    currency: number;
    currentPrice: number;
    holdingValue: number;
    mwPercent: number;          // money weighted performance
    holdingPercent: number;
    a_payout: number;           // anualized payout
    n_payout: number;           // next year payout
    ytd_payout: number;         // payout yard to date
    distributions:IDividend[];

    get price() { return Prices.i.get(this.id) || Prices.i.CASH }
    
    constructor(portfolio:Portfolio, id:string, json:ITransRow[]) {
        this.portfolio = portfolio;
        this.id = id;
        this.transactions = [];

        for(let i=0; i<json.length; i++) {
            this.transactions.push(createTrans(this.id, this, json[i]))
        }

        this.transactions.sort((l,r) => {
            if (l.date.getTime()!=r.date.getTime()) {
                return l.date<r.date ? -1 : 1;
            }
            if (l.accountId!=r.accountId) return l.accountId - r.accountId;
            else if (l.type!=r.type) return r.type - l.type;
            else return r.quantity - l.quantity;
        })

        this.quantity = 0;
        this.averagePrice = 0;
        this.dividend = 0;
        this.originalCost = 0;
        this.adjustments = 0;
        this.holdingValue = 0;
        this.mwPercent = 0;
        this.holdingCost = 0;
        this.sumTransaction = 0;
        this.gain = 0;
        this.holdingPercent = 0;
        this.a_payout = 0;
        this.ytd_payout = 0;
        this.excessDiv = 0;
        this.profit = 0;
        this.ugain = 0;
        this.n_payout= 0;
        this.currency = 0
        this.currentPrice = 0;
        this.name = "";
        this.distributions = []
        
        this.setPrice()
        this.updateTransactions();
        this.computePerformance();
        this.computePayout()
    }

    get isEquity(){ return !this.isCashEq }
    get isSave(){ return Price.isSave(this.id) }
    get isGIC(){ return Price.isGIC(this.id) }
    get isGIC_or_bond(){ return Price.isGIC(this.id) || Price.isBond(this.id) }
    get isBond(){ return Price.isBond(this.id) }
    get isCash(){ return Price.isCash(this.id) }
    get isCashEq(){ return this.isCash || this.isSave || this.isBond || this.isGIC_or_bond }
    get currencySign() { return this.currency==0 ? "C" : "U" }
    get buysellTrans() { return this.transactions.filter(t => t.isBuyOrSell).sort((l,r) => l.date.getTime()-r.date.getTime())}
    get distribution(){
        const freq = this.price.frequency
        switch(freq){
        case "m": return this.payout*this.quantity/12
        case "q": return this.payout*this.quantity/4
        case "s": return this.payout*this.quantity/2
        default: return this.payout*this.quantity
        }
    }

    transaction(id:string){ return this.transactions.find(t => t._id==id)}

    setPrice(){
        const price = this.price
        this.currency = price.currency
        this.name = `${price.name} (${this.id})`;
        this.currency = price.currency;
        this.currentPrice = price.price;
        return this
    }

    updateTransactions(){
        for(let transaction of this.transactions) {
            transaction.gain = 0
            this.updateTransaction(transaction);
        }
        if (PortUtils.isZero(this.quantity)) this.quantity = 0;
        return this;
    }

    value(col:string, toCad:boolean=false) : string{
        const {currency,exchange} = this.getExchange(toCad)
        switch(col){
        case Cols.name:             return this.name;
        case Cols.quantity:         return PortUtils.formatNumber(this.quantity)
        case Cols.averagePrice:     return PortUtils.formatCurrency(currency, this.averagePrice * exchange);
        case Cols.holdingCost:      return PortUtils.formatCurrency(currency, this.holdingCost * exchange);
        case Cols.currentPrice:     return PortUtils.formatCurrency(currency, this.currentPrice * exchange);
        case Cols.holdingValue:     return PortUtils.formatCurrency(currency, this.holdingValue * exchange);
        case Cols.dividend:         return PortUtils.formatCurrency(currency, this.dividend * exchange);
        case Cols.gain:             return PortUtils.formatCurrency(currency, this.gain * exchange, false, true);
        case Cols.originalCost:     return PortUtils.formatCurrency(currency, this.originalCost * exchange);
        case Cols.payout:           return PortUtils.formatCurrency(currency, this.payout * exchange)
        case Cols.a_payout:         return PortUtils.formatCurrency(currency, this.a_payout * exchange);
        case Cols.n_payout:         return PortUtils.formatCurrency(currency, this.n_payout * exchange);
        case Cols.ytd_payout:       return PortUtils.formatCurrency(currency, this.ytd_payout * exchange);
        case Cols.yield:            return PortUtils.formatPercent(this.yield * exchange)
        case Cols.holdingPercent:   return PortUtils.formatPercent(this.holdingPercent, true);
        case Cols.mwPercent:        return PortUtils.formatPercent(this.mwPercent, true);
        case Cols.excessDiv:        return PortUtils.formatCurrency(currency, this.excessDiv * exchange, false, true);
        case Cols.profit:           return PortUtils.formatCurrency(currency, this.profit * exchange);
        case Cols.ugain:            return PortUtils.formatCurrency(currency, this.ugain * exchange, false, true)
        default:                    return "";
        }
    }
    rawValue(col:string, toCad:boolean=false) : string|number {
        const {exchange} = this.getExchange(toCad)
        switch(col){
        case Cols.averagePrice:     
        case Cols.holdingCost:      
        case Cols.currentPrice:     
        case Cols.holdingValue:     
        case Cols.dividend:         
        case Cols.gain:             
        case Cols.originalCost:     
        case Cols.payout:           
        case Cols.a_payout:         
        case Cols.n_payout:         
        case Cols.ytd_payout:       
        case Cols.excessDiv:        
        case Cols.profit:           
        case Cols.ugain:        return (this as any)[col]*exchange
        default:                return (this as any)[col]
        }
    }
    getExchange(toCad:boolean) {
        const currency = toCad ? 0 : this.currency;
        return {currency,exchange:toCad ? this.exchange : 1}
    }
    tooltip(col:string, toCad:boolean=false) : string|undefined {
        const {currency,exchange} = this.getExchange(toCad)
        switch(col){
        case Cols.mwPercent:     
            return `${PortUtils.formatCurrency(currency, this.gain*exchange)} / ${PortUtils.formatCurrency(currency, this.originalCost*exchange)}`;
        default:                    
            return undefined;
        }
    }
    get exchange() { return this.currency ? Prices.i.USD2CAD : 1}
    get payout(){ return this.price.payout}
    get yield(){ return this.price.payout/this.price.price*100 }
    get upcomingDividend(){
        const price = this.price
        const trans = this.buysellTrans
        const ex = new Date(Date.now() - price.ex*24*3600*1000)
        let qty = 0;
        for(let i=0; i<trans.length; i++) {
            if (trans[i].date<=ex) qty += trans[i].quantity
            else break;
        }
        return qty * price.payout / price.numDistributions
    }
    compValue(col:string) : number|string {
        switch(col){
        case Cols.holdingValue: return this.holdingValue * this.exchange
        case Cols.currentPrice: return this.currentPrice * this.exchange;
        case Cols.holdingCost:  return this.holdingCost * this.exchange;
        case Cols.averagePrice: return this.averagePrice * this.exchange;
        case Cols.dividend:     return this.dividend * this.exchange;
        case Cols.payout:       return this.price.payout * this.exchange;
        case Cols.a_payout:     return this.a_payout * this.exchange;
        case Cols.n_payout:     return this.n_payout * this.exchange;
        case Cols.ytd_payout:   return this.ytd_payout * this.exchange;
        case Cols.yield:        return this.yield
        default:                return (this as any)[col]
        }
    }
    className(col:string) : string {
        if (col==Cols.mwPercent) return this.mwPercent>=0 ? "green" : "red"
        else if (col==Cols.holdingPercent) return this.holdingPercent>=0 ? "green" : "red";
        else if (col==Cols.gain) return this.gain>=0 ? "green" : "red";
        else if (col==Cols.profit) return this.profit>=0 ? "green" : "red";
        else if (col==Cols.name) { return "symbol" }
        else if (col==Cols.dividend) { return "blue" }
        else if (col==Cols.ugain) return this.ugain>=0 ? "green" : "red";
        else return ""
    }
    protected cumulateSum(transaction:Transaction){
        if (transaction.type==TransactionType.buy && transaction.quantity>0) this.originalCost += transaction.quantity * transaction.price;
        switch(transaction.type){
        case TransactionType.buy:
        case TransactionType.reinvest:
        case TransactionType.topup:
            this.sumTransaction -= transaction.quantity * transaction.price;
            break;
        case TransactionType.dividend:
        case TransactionType.interest:
            this.sumTransaction += transaction.quantity*transaction.price;
            transaction.gain = transaction.quantity*transaction.price;
            break;
        case TransactionType.adjust:
            break;
        }
    }

    computeHoldingValue(){
        this.holdingValue = this.quantity*this.currentPrice;
        return this;
    }
    computePerformance(){
        this.holdingCost = this.averagePrice*this.quantity;
        this.computeHoldingValue()
        this.gain = this.sumTransaction + this.holdingValue;
        this.mwPercent = this.originalCost ? (this.gain / this.originalCost * 100) : 0;
        this.holdingPercent = this.holdingCost ? (this.holdingValue-this.holdingCost)/this.holdingCost * 100 : 0;
        this.profit = this.transactions.reduce((p,t)=>p+t.gain,0)
        this.ugain = this.holdingValue - this.holdingCost;
        let reinvest = this.transactions.reduce((p,trans) => p+(trans.isReinvest ? trans.quantity*trans.price : 0), 0)
        this.excessDiv = this.dividend - reinvest;
        return this;
    }

    get dividendHistory(){return new DividendHistory(this.transactions)}

    private _createDist(account:string, date:Date,amount:number,qty:number,future:boolean) : IDividend {
        return {id:`${this.id} (${account})`,date,amount,holding:this,qty,future}
    }
    computePayout(){
        this.resetPayout().computePayoutPerAccount()
    }

    resetPayout(){
        this.distributions.length = 0;
        this.a_payout = 0;
        this.n_payout = 0;
        this.ytd_payout = 0
        return this
    }
    computePayoutPerAccount(){
        const accountIds = Array.from(new Set(this.transactions.map(t => t.accountId)))
        for(const accountId of accountIds) {
            const trans = this.transactions.filter(t => t.accountId==accountId)
            this._computePayout(accountId, this.price, new DividendHistory(trans))
        }
        return this
    }

    protected _computePayout(accountId:number, price:Price, hist:DividendHistory) {
        const dates = price.dates;
        const count = price.numDistributions
        const account = this.portfolio.accountName(accountId)
        
        this.ytd_payout += hist.ytd;
        if (dates.length==0) return this
        const now = new Date(), year = now.getFullYear()
        for(let i=0; i<dates.length; i+=2) {
            const ex = dates[i], date = dates[i+1];
            const div = hist.dividend(date)
            const qty = hist.quantity(ex)
            const thisYear = year==date.getFullYear()
            if (div && thisYear) {
                this.distributions.push(this._createDist(account, date, div, qty, false))
                this.a_payout += div
            }
            else if (PortUtils.isZero(qty)) continue
            else if (price.variableDividend){
                const payout = div || hist.dividend(PortUtils.dateAdd(date, {year:-1})) || hist.dividend(PortUtils.dateAdd(date, {year:-2}))
                if (PortUtils.isZero(payout)) continue
                
                this.distributions.push(this._createDist(account, date,payout,thisYear?qty:hist.currentQuantity,true))
                if (thisYear) this.a_payout += payout
                else this.n_payout += payout
            }
            else{
                const payout = price.payout/count
                if (PortUtils.isZero(payout)) continue;

                const quantity = thisYear ? qty : hist.currentQuantity
                const amount = quantity*payout
                this.distributions.push(this._createDist(account, date,amount,quantity,true))
                if (thisYear) this.a_payout += amount
                else this.n_payout += amount
            }
        }

        return this;
    }

    createHolding(history:Transaction[]=[]) : Holding { return createHolding(this.portfolio, this.id, history) }

    shouldFilter(search:string){ 
        return this.id.toLowerCase().indexOf(search)>=0 || this.name.toLowerCase().indexOf(search)>=0
    }

    private _filterByAccount(accountId:number) : Holding {
        const history = this.transactions.filter(trans => trans.accountId==accountId ? trans : null)
        if (history.length==0) return this.createHolding()
        else return this.createHolding(history)
    }
    public filterByAccount(accountId:number) : Holding {
        if (accountId<0) return this
        else return this._filterByAccount(accountId)
    }
    private updateTransaction(transaction:Transaction){
        this.cumulateSum(transaction);
        switch(transaction.type){
        case TransactionType.buy:
        case TransactionType.reinvest:
        case TransactionType.topup:
            if (transaction.quantity>0) {
                const avgCost = this.quantity * this.averagePrice;
                const cost = transaction.price * transaction.quantity;
                this.averagePrice = (avgCost + cost)/(this.quantity + transaction.quantity);
                this.holdingCost += cost;
                this.quantity += transaction.quantity;
            }
            else {
                transaction.gain += (-transaction.quantity) * (transaction.price - this.averagePrice);
                this.quantity += transaction.quantity;
                if (PortUtils.isZero(this.quantity)) this.holdingCost = 0;
            }
            break;
        case TransactionType.adjust:
            const cost = this.quantity * this.averagePrice;
            this.quantity += transaction.quantity;
            if (PortUtils.isZero(this.quantity)) this.holdingCost = 0;
            else {
                this.averagePrice = cost/this.quantity;
                this.holdingCost = cost*this.quantity;
            }
            break;
        case TransactionType.dividend:
        case TransactionType.interest:
            this.dividend += transaction.quantity;
            break;
        case TransactionType.adjust:
            this.adjustments += transaction.quantity;
            break;
        default:
            console.log(this.id, "Unknown transaction type ", transaction.type, transaction)
        }
    }

    static cloneTransaction(trans:Transaction|null|undefined, holding:Holding|null|undefined, ticker:string){
        if (trans) return trans.clone()
        
        const portfolio = PortAccount.i.portfolio
        if (!holding) holding = portfolio.holding(ticker) ?? createHolding(portfolio, ticker, [])
        
        return createTrans(ticker, holding, {} as any)
    }
}
